import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ImageIcon from "@mui/icons-material/Image";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { EmptyState } from "../../../components/common/EmptyState";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { resolveEmployeeId } from "../../../utils/employeeIdentity";

const PAGE_BG = "#f5f7fb";
const CARD_BORDER = "#e5e7eb";
const DEMO_EMP_ID = "6";

const STATUS_FILTERS = [
  { value: "all", label: "전체" },
  { value: "open", label: "진행중" },
  { value: "overdue", label: "기한초과" },
  { value: "done", label: "완료" },
];

const getNowInput = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getCompletedDisplay = (action = {}) => {
  const value = action.completed_at || action.completed_at_input;
  if (!value) return "-";
  return String(value).replace("T", " ").slice(0, 16);
};

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const getStatusMeta = (status) => {
  if (status === "done") {
    return {
      label: "완료",
      color: "success",
      icon: <CheckCircleIcon fontSize="small" />,
      bg: "#ecfdf5",
      fg: "#047857",
    };
  }
  if (status === "overdue") {
    return {
      label: "기한초과",
      color: "error",
      icon: <ErrorOutlineIcon fontSize="small" />,
      bg: "#fef2f2",
      fg: "#b91c1c",
    };
  }
  return {
    label: "진행중",
    color: "warning",
    icon: <PendingActionsIcon fontSize="small" />,
    bg: "#fff7ed",
    fg: "#d97706",
  };
};

const getActionSourceMeta = (action = {}) => {
  if (action.inspection_id || action.inspection_content || action.inspected_day) {
    return {
      label: "안전점검 유입",
      description: "안전점검 기록에서 조치요청으로 생성된 항목입니다.",
      color: "#2563eb",
      bg: "#eff6ff",
      border: "#bfdbfe",
    };
  }

  return {
    label: "직접 등록",
    description: "관리자가 직접 등록한 조치 항목입니다.",
    color: "#475569",
    bg: "#f8fafc",
    border: "#e2e8f0",
  };
};

function FilterButton({ active, children, onClick }) {
  return (
    <Button
      size="small"
      variant={active ? "contained" : "outlined"}
      onClick={onClick}
      sx={{
        minHeight: 36,
        borderRadius: 2,
        px: 1.5,
        fontWeight: 800,
        whiteSpace: "nowrap",
        flexShrink: 0,
        borderColor: active ? "primary.main" : CARD_BORDER,
        bgcolor: active ? "primary.main" : "#fff",
      }}
    >
      {children}
    </Button>
  );
}

function SummaryTile({ icon, label, value, color = "#2563eb" }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        minHeight: { xs: 74, sm: 112 },
        boxSizing: "border-box",
        minWidth: 0,
        border: "1px solid",
        borderColor: CARD_BORDER,
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction={{ xs: "row", sm: "column" }}
        spacing={1.25}
        alignItems="center"
        justifyContent={{ xs: "space-between", sm: "center" }}
        textAlign={{ xs: "left", sm: "center" }}
        sx={{ height: "100%", minWidth: 0 }}
      >
        <Avatar sx={{ width: 38, height: 38, bgcolor: `${color}14`, color, flexShrink: 0 }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", fontSize: { xs: 22, sm: 24 }, lineHeight: 1.1 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function ActionCard({ action, onComplete }) {
  const status = getStatusMeta(action.effective_sla_status || action.sla_status);
  const source = getActionSourceMeta(action);
  const done = status.label === "완료";
  const photoUrl = resolvePhotoUrl(action.photo_url);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        borderRadius: 2,
        borderColor: CARD_BORDER,
        bgcolor: "#fff",
        overflow: "hidden",
        borderLeft: "4px solid",
        borderLeftColor: done ? "#16a34a" : status.fg,
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.25 } }}>
        <Stack spacing={{ xs: 1.35, sm: 1.75 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
            <Box sx={{ minWidth: 0 }}>
              <Chip
                size="small"
                label={source.label}
                title={source.description}
                sx={{
                  height: 24,
                  mb: 0.75,
                  borderRadius: "7px",
                  bgcolor: source.bg,
                  border: "1px solid",
                  borderColor: source.border,
                  color: source.color,
                  fontWeight: 900,
                  "& .MuiChip-label": { px: 0.9 },
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  color: "#111827",
                  lineHeight: 1.35,
                  overflowWrap: "anywhere",
                  wordBreak: "keep-all",
                }}
              >
                {action.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                점검 {action.inspected_day || "-"} {action.inspected_time || ""}
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={status.icon}
              color={status.color}
              label={status.label}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            <ScheduleIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 800, color: status.fg }}>
              기한 {action.due_day || "-"} {action.due_time || ""}
            </Typography>
          </Stack>

          {done ? (
            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
              <CheckCircleIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 800, color: "#047857" }}>
                완료 일시 {getCompletedDisplay(action)}
              </Typography>
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            <PersonIcon fontSize="small" />
            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              담당 {action.assignee_name || action.assignee_emp_id || "-"}
              {action.assignee_department ? ` / ${action.assignee_department}` : ""}
            </Typography>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              점검 내용
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#374151",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {action.inspection_content || "점검 내용 없음"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              조치 지시
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#374151",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {action.instruction || "조치 지시 내용 없음"}
            </Typography>
          </Box>

          {done ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {action.action_result || "완료 처리된 조치입니다."}
            </Alert>
          ) : null}
        </Stack>
      </CardContent>
      <CardActions
        sx={{
          px: { xs: 1.5, sm: 2.25 },
          pb: { xs: 1.5, sm: 2 },
          pt: 0,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
          gap: 1,
          minWidth: 0,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-start", sm: "initial" } }}>
          {photoUrl ? (
            <Tooltip title="현장 사진 열기">
              <IconButton
                size="small"
                component="a"
                href={photoUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ border: "1px solid", borderColor: CARD_BORDER, borderRadius: 2 }}
              >
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
        {!done ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={() => onComplete(action)}
            sx={{ flexShrink: 0, minWidth: 72, width: { xs: "100%", sm: "auto" } }}
          >
            완료
          </Button>
        ) : null}
      </CardActions>
    </Card>
  );
}

function CompleteDialog({ open, action, value, completedAt, isSaving, onValueChange, onDateChange, onClose, onSubmit }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={false}>
      <DialogTitle sx={{ pb: 1, pt: { xs: 2.5, sm: 2 }, px: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              조치 완료
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {action?.title || ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: PAGE_BG, px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="완료 내용"
            placeholder="예: 누유 부위 밸브 교체 완료, 주변 바닥 세척 및 재발 방지 점검 추가"
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
          />
          <TextField
            fullWidth
            type="datetime-local"
            label="완료 일시"
            value={completedAt}
            onChange={(event) => onDateChange(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
        <Button color="inherit" onClick={onClose} sx={{ flex: { xs: 1, sm: "initial" } }}>
          취소
        </Button>
        <Button variant="contained" disabled={isSaving} onClick={onSubmit} sx={{ flex: { xs: 1, sm: "initial" }, fontWeight: 900 }}>
          {isSaving ? "처리 중" : "완료 처리"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ActionInboxPage({ mode = "mine" }) {
  const { group_code, project_code } = useParams();
  const [searchParams] = useSearchParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = useSelector((state) => state.auth.user);
  const queryEmpId = searchParams.get("emp_id") || "";
  const assigneeEmpId = resolveEmployeeId({ user, queryEmpId }) || DEMO_EMP_ID;
  const isAdminMode = mode === "all";
  const scope = isAdminMode ? "all" : "mine";
  const needsEmployeeLink = !isAdminMode && !assigneeEmpId;

  const [status, setStatus] = useState("all");
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeText, setCompleteText] = useState("");
  const [completedAt, setCompletedAt] = useState(getNowInput());

  const actionsQuery = useQuery({
    queryKey: [
      "inspection-action-inbox",
      group_code,
      project_code,
      scope,
      status,
      isAdminMode ? "all" : assigneeEmpId || "unresolved",
    ],
    queryFn: async () => {
      if (needsEmployeeLink) return [];
      const params = { scope, status };
      if (!isAdminMode) {
        params.assignee_emp_id = assigneeEmpId;
      }
      const response = await api.get("/inspection/actions", {
        params,
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && (isAdminMode || assigneeEmpId)),
    placeholderData: [],
  });

  const actions = useMemo(() => actionsQuery.data || [], [actionsQuery.data]);
  const summary = useMemo(() => {
    const done = actions.filter((item) => (item.effective_sla_status || item.sla_status) === "done").length;
    const overdue = actions.filter((item) => (item.effective_sla_status || item.sla_status) === "overdue").length;
    const open = actions.filter((item) => {
      const itemStatus = item.effective_sla_status || item.sla_status;
      return itemStatus !== "done" && itemStatus !== "overdue";
    }).length;
    return { total: actions.length, open, overdue, done };
  }, [actions]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["inspection-action-inbox", group_code, project_code] });
    queryClient.invalidateQueries({ queryKey: ["risk-inspection-logs", group_code, project_code] });
  };

  const completeMutation = useMutation({
    mutationFn: async () =>
      api.put(`/inspection/actions/${completeTarget.id}/complete`, {
        action_result: completeText,
        completed_at: completedAt,
      }),
    onSuccess: () => {
      toast.success("조치가 완료 처리되었습니다.");
      setCompleteTarget(null);
      setCompleteText("");
      setCompletedAt(getNowInput());
      invalidate();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "조치 완료 처리 중 오류가 발생했습니다.");
    },
  });

  const openCompleteDialog = (action) => {
    setCompleteTarget(action);
    setCompleteText(action.action_result || "");
    setCompletedAt(action.completed_at_input || getNowInput());
  };

  const submitComplete = () => {
    if (!String(completeText || "").trim()) {
      toast.warning("완료 내용을 입력해주세요.");
      return;
    }
    completeMutation.mutate();
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: PAGE_BG,
        p: { xs: 1.25, md: 3 },
        overflowX: "hidden",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
      }}
    >
      <Stack
        spacing={{ xs: 1.75, md: 2.25 }}
        sx={{
          width: "100%",
          maxWidth: { xs: "calc(100vw - 20px)", md: 1280 },
          minWidth: 0,
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            position: { xs: "sticky", md: "static" },
            top: 0,
            zIndex: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: CARD_BORDER,
            bgcolor: "#fff",
            backdropFilter: "blur(10px)",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar sx={{ bgcolor: "#2563eb14", color: "#2563eb" }}>
                <AssignmentTurnedInIcon />
              </Avatar>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 900, color: "#111827" }}>
                {isAdminMode ? "후속 조치 관리" : "내 조치 업무"}
              </Typography>
            </Stack>

            <Stack spacing={1} alignItems={{ xs: "stretch", md: "flex-end" }} sx={{ minWidth: 0, maxWidth: "100%" }}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap={{ xs: "nowrap", md: "wrap" }}
                useFlexGap
                sx={{ overflowX: { xs: "auto", md: "visible" }, pb: 0.25, maxWidth: "100%", minWidth: 0 }}
              >
                {STATUS_FILTERS.map((item) => (
                  <FilterButton key={item.value} active={status === item.value} onClick={() => setStatus(item.value)}>
                    {item.label}
                  </FilterButton>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        {needsEmployeeLink ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            로그인 계정에 직원 정보가 연결되지 않아 내 조치 업무를 불러올 수 없습니다. 계정의 직원 정보를 연결한 뒤 다시 확인해주세요.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            gap: { xs: 1, md: 1.5 },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile icon={<AssignmentTurnedInIcon />} label="전체" value={`${summary.total}건`} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile icon={<PendingActionsIcon />} label="진행중" value={`${summary.open}건`} color="#d97706" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile icon={<ErrorOutlineIcon />} label="기한초과" value={`${summary.overdue}건`} color="#dc2626" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile icon={<CheckCircleIcon />} label="완료" value={`${summary.done}건`} color="#16a34a" />
          </Box>
        </Box>

        {actionsQuery.isError ? (
          <Alert severity="error">조치함을 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        {actionsQuery.isLoading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : null}

        {!actionsQuery.isLoading && !actions.length ? (
          <EmptyState
            compact
            icon={<FactCheckIcon />}
            title="표시할 조치가 없습니다"
            description={
              needsEmployeeLink
                ? "계정에 직원 정보가 연결되면 배정된 조치가 이곳에 표시됩니다."
                : isAdminMode
                ? "안전점검에서 조치요청이 등록되면 이곳에 표시됩니다."
                : "배정된 조치가 생기면 이곳에 표시됩니다."
            }
          />
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
            gap: 1.5,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          {actions.map((action) => (
            <Box key={action.id} sx={{ minWidth: 0 }}>
              <ActionCard action={action} onComplete={openCompleteDialog} />
            </Box>
          ))}
        </Box>
      </Stack>

      <CompleteDialog
        open={Boolean(completeTarget)}
        action={completeTarget}
        value={completeText}
        completedAt={completedAt}
        isSaving={completeMutation.isPending}
        onValueChange={setCompleteText}
        onDateChange={setCompletedAt}
        onClose={() => setCompleteTarget(null)}
        onSubmit={submitComplete}
      />
    </Box>
  );
}

export function WorkerActionInbox() {
  return <ActionInboxPage mode="mine" />;
}

export function AdminActionInbox() {
  return <ActionInboxPage mode="all" />;
}
